<template>
  <div class="route-card">
    <!-- Card header with gradient -->
    <div class="card-header">
      <div class="header-left">
        <div class="header-icon">
          <span class="material-symbols-outlined">directions_bus</span>
        </div>
        <div class="header-info">
          <h3 class="header-title">{{ route.name }}</h3>
          <p class="header-subtitle">Route ID: {{ route.id }}</p>
        </div>
      </div>
      <button 
        @click="toggleFavorite" 
        class="favorite-btn"
        :class="{ 'is-favorite': isFavorite }"
        :aria-label="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
      >
        <span class="material-symbols-outlined" :style="isFavorite ? 'font-variation-settings: \'FILL\' 1' : ''">
          star
        </span>
      </button>
    </div>

    <div class="card-body">
      <!-- Days of operation -->
      <div class="days-section">
        <div class="section-label">
          <span class="material-symbols-outlined">calendar_month</span>
          <span>Days of Operation</span>
        </div>
        <div class="days-badges">
          <span 
            v-for="day in route.days" 
            :key="day" 
            class="day-badge"
            :class="{ 'today': isToday(day) }"
          >
            {{ day.substring(0, 3) }}
          </span>
        </div>
      </div>

      <!-- Bus numbers -->
      <div v-if="route.buses && route.buses.length" class="buses-section">
        <div class="section-label">
          <span class="material-symbols-outlined">directions_bus</span>
          <span>Bus Numbers</span>
        </div>
        <div class="buses-badges">
          <span v-for="bus in route.buses" :key="bus" class="bus-badge">
            {{ bus }}
          </span>
        </div>
      </div>

      <!-- Locations grid -->
      <div class="locations-grid">
        <!-- Pick-up locations -->
        <div class="location-section pickup">
          <div class="location-header">
            <span class="material-symbols-outlined">location_on</span>
            <span>Pick-up Points</span>
          </div>
          <ul class="location-list">
            <li v-for="(loc, i) in route.pickup_locations" :key="`pickup-${i}`">
              <span class="material-symbols-outlined">arrow_right_alt</span>
              <span>{{ loc }}</span>
            </li>
          </ul>
        </div>

        <!-- Drop-off locations -->
        <div class="location-section dropoff">
          <div class="location-header">
            <span class="material-symbols-outlined">flag</span>
            <span>Drop-off Points</span>
          </div>
          <ul class="location-list">
            <li v-for="(loc, i) in route.dropoff_locations" :key="`dropoff-${i}`">
              <span class="material-symbols-outlined">arrow_right_alt</span>
              <span>{{ loc }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Today's schedule and next bus -->
      <div v-if="getTodayTimes(route).length > 0" class="schedule-section">
        <div class="schedule-header">
          <div class="section-label">
            <span class="material-symbols-outlined">schedule</span>
            <span>Today's Schedule</span>
          </div>
          
          <!-- Next bus info -->
          <div v-if="getNextBus(route).nextTime" class="next-bus-badge" :class="getNextBus(route).color">
            <span class="material-symbols-outlined">alarm</span>
            <div class="next-bus-info">
              <span class="next-bus-label">Next:</span>
              <span class="next-bus-time">{{ getNextBus(route).timeStr }}</span>
              <span class="next-bus-countdown">{{ getNextBus(route).countdownStr }}</span>
            </div>
          </div>
          <div v-else class="no-bus-badge">
            <span class="material-symbols-outlined">event_busy</span>
            <span>No more buses today</span>
          </div>
        </div>

        <div class="times-grid">
          <span 
            v-for="(time, i) in getTodayTimes(route)" 
            :key="i"
            class="time-badge"
            :class="getTimeBadgeClass(time)"
          >
            {{ time }}
          </span>
        </div>
      </div>
      
      <!-- No service today -->
      <div v-else class="no-service">
        <span class="material-symbols-outlined">event_busy</span>
        <span>No service today</span>
        <p class="no-service-hint">{{ getNextBus(route).dayInfo }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RouteCard',
  props: {
    route: {
      type: Object,
      required: true
    },
    favoriteRoutes: {
      type: Array,
      default: () => []
    },
    getNextBus: {
      type: Function,
      required: true
    },
    getTodayTimes: {
      type: Function,
      required: true
    }
  },
  computed: {
    isFavorite() {
      return this.favoriteRoutes.includes(this.route.id);
    }
  },
  methods: {
    toggleFavorite() {
      this.$emit('toggleFavorite', this.route.id);
    },
    
    isToday(day) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = days[new Date().getDay()];
      return day === today;
    },
    
    getTimeBadgeClass(time) {
      const now = new Date();
      const [hours, minutes] = time.split(':').map(Number);
      const timeDate = new Date();
      timeDate.setHours(hours, minutes, 0, 0);
      
      // If time is past
      if (timeDate < now) {
        return 'past';
      }
      
      // If time is within the next 30 minutes
      const diffMs = timeDate - now;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins <= 5) {
        return 'urgent';
      } else if (diffMins <= 30) {
        return 'soon';
      } else {
        return 'upcoming';
      }
    }
  }
};
</script>
<style scoped>
.route-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.route-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

/* Card Header */
.card-header {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  backdrop-filter: blur(10px);
}

.header-icon .material-symbols-outlined {
  font-size: 28px;
  color: white;
}

.header-info {
  flex: 1;
  min-width: 0;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
  text-overflow: ellipsis;
  overflow: hidden;
}

.header-subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0.25rem 0 0 0;
}

.favorite-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.favorite-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.favorite-btn .material-symbols-outlined {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.favorite-btn.is-favorite {
  background: rgba(250, 204, 21, 0.3);
  border-color: rgba(250, 204, 21, 0.5);
}

.favorite-btn.is-favorite .material-symbols-outlined {
  color: #fbbf24;
}

/* Card Body */
.card-body {
  padding: 1.5rem;
}

/* Section Labels */
.section-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-label .material-symbols-outlined {
  font-size: 20px;
  color: #3b82f6;
}

/* Days Section */
.days-section {
  margin-bottom: 1.5rem;
}

.days-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-badge {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.day-badge.today {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* Buses Section */
.buses-section {
  margin-bottom: 1.5rem;
}

.buses-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.bus-badge {
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  border: 1px solid #93c5fd;
}

/* Locations Grid */
.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.location-section {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1rem;
}

.location-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
}

.location-section.pickup .location-header .material-symbols-outlined {
  color: #10b981;
}

.location-section.dropoff .location-header .material-symbols-outlined {
  color: #ef4444;
}

.location-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.location-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: #4b5563;
}

.location-list li .material-symbols-outlined {
  font-size: 16px;
  color: #9ca3af;
}

/* Schedule Section */
.schedule-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #bae6fd;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.next-bus-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.next-bus-badge.bg-green-600 {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.next-bus-badge.bg-yellow-500 {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.next-bus-badge.bg-red-600 {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.next-bus-badge .material-symbols-outlined {
  font-size: 24px;
}

.next-bus-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.next-bus-label {
  font-size: 0.625rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.next-bus-time {
  font-size: 1.125rem;
  font-weight: 700;
}

.next-bus-countdown {
  font-size: 0.75rem;
  opacity: 0.9;
}

.no-bus-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
}

.times-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
}

.time-badge {
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  text-align: center;
  transition: all 0.2s ease;
  font-family: 'Courier New', monospace;
}

.time-badge.past {
  background: #f3f4f6;
  color: #9ca3af;
  text-decoration: line-through;
}

.time-badge.urgent {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.time-badge.soon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.time-badge.upcoming {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

/* No Service */
.no-service {
  background: #f9fafb;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.no-service .material-symbols-outlined {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 0.5rem;
}

.no-service span {
  font-weight: 600;
  font-size: 1rem;
  display: block;
  margin-bottom: 0.5rem;
}

.no-service-hint {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0.5rem 0 0 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Responsive */
@media (max-width: 640px) {
  .card-header {
    padding: 1.25rem;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .header-icon .material-symbols-outlined {
    font-size: 24px;
  }

  .header-title {
    font-size: 1.125rem;
  }

  .favorite-btn {
    width: 40px;
    height: 40px;
  }

  .locations-grid {
    grid-template-columns: 1fr;
  }

  .schedule-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .times-grid {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  }
}
</style>